<script setup lang="ts">
interface Props {
  status: 'awaitingNetwork' | 'enqueued' | 'uploading' | 'failed' | 'uploaded'
  progress: number;
  totalBytes: number;
}

const props = defineProps<Props>();

const { t } = useEkI18n();

const icon = computed(() => {
  switch (props.status) {
    case 'awaitingNetwork': return ioniconsWifi;
    case 'enqueued': return ioniconsArrowUpCircle;
    case 'uploading': return ioniconsCloudUpload;
    case 'failed': return ioniconsWarning;
    case 'uploaded': return ioniconsCheckmarkCircle;
    default: return ioniconsAlertCircleOutline;
  }
});

const color = computed<string>(() => {
  switch (props.status) {
    case 'awaitingNetwork': return 'warning';
    case 'enqueued': return 'warning';
    case 'uploading': return 'primary';
    case 'failed': return 'danger';
    case 'uploaded': return 'success';
    default: return 'medium';
  }
});

const statusText = computed(() => {
  switch (props.status) {
    case 'awaitingNetwork': return t('documents.status.awaitingNetwork');
    case 'enqueued': return t('documents.status.enqueued');
    case 'uploading': return t('documents.status.uploading');
    case 'failed': return t('documents.status.failed');
    case 'uploaded': return t('documents.status.uploaded');
    default: return '';
  }
});

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;

  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;

  return `${bytes} B`;
}

const sizeLabel = computed(() => {
  if (props.status !== 'uploading' || props.totalBytes === 0) return null;
  const loaded = Math.round(props.progress * props.totalBytes);

  return `${formatBytes(loaded)} / ${formatBytes(props.totalBytes)}`;
});

</script>

<template>
  <div class="flex ion-align-items-center">
    <IonIcon
      class="mr-1"
      :color="color"
      :icon="icon"
    />
    <IonText
      :color="color"
      class="subtitle"
    >
      {{ statusText }}
    </IonText>
  </div>
  <IonProgressBar
    v-if="status === 'uploading'"
    :value="progress"
  />
  <IonText
    v-if="sizeLabel"
    :color="color"
    class="size-label"
  >
    {{ sizeLabel }}
  </IonText>
</template>

<style scoped>
.subtitle {
    font-size: smaller;
}

.size-label {
    font-size: smaller;
}
</style>
